import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Info,
  Search
} from 'lucide-react';
import Alert from '../Alert/Alert';
import { formatRoleName, USER_ROLES } from '../../../utils/constants';
import NotificationDropdown from './NotificationDropdown';
import SearchMenu from '../Sidebar/SearchMenu';
import essService from '../../../api/services/essService';
import AICopilotAvatar from '../AICopilotAvatar';

const Header = ({
  onToggleSidebar,
  isSidebarCollapsed,
  user,
  onLogout,
  onNavigate,
  searchTerm,
  onSearchChange
}) => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLogoutAlert, setShowLogoutAlert] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [essProfile, setEssProfile] = useState(null);

  const userMenuRef = useRef(null);
  const searchRef = useRef(null);

  const fetchESSProfile = async () => {
    try {
      const res = await essService.getProfile();
      if (res && res.success && res.data) {
        setEssProfile(res.data);
      }
    } catch (e) { }
  };

  useEffect(() => {
    fetchESSProfile();

    const handleProfileUpdate = () => {
      fetchESSProfile();
    };

    window.addEventListener('user-profile-updated', handleProfileUpdate);
    return () => {
      window.removeEventListener('user-profile-updated', handleProfileUpdate);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target) && !event.target.closest('.search-toggle-btn')) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      } else if (e.key === 'Escape') {
        setIsSearchOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const getUserWithPermissions = () => {
    if (user && user.granularPermissions) return user;
    try {
      const userData = localStorage.getItem('user_data');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        if (parsedUser && parsedUser.granularPermissions) return parsedUser;
      }
    } catch (e) { }
    return user;
  };

  const userWithPermissions = getUserWithPermissions();
  const userRole = userWithPermissions?.role || user?.role || USER_ROLES.EMPLOYEE;

  const handleLogoutClick = () => {
    setShowLogoutAlert(true);
    setShowUserMenu(false);
  };

  const getProfileImgSrc = () => {
    const raw =
      user?.systemInfo?.profilePicture ||
      user?.personalInfo?.profileImage ||
      user?.personalInfo?.profilePicture ||
      user?.profilePicture ||
      user?.profileImage ||
      user?.avatar ||
      userWithPermissions?.systemInfo?.profilePicture ||
      userWithPermissions?.personalInfo?.profileImage ||
      userWithPermissions?.profilePicture ||
      userWithPermissions?.profileImage ||
      essProfile?.systemInfo?.profilePicture ||
      essProfile?.personalInfo?.profileImage ||
      essProfile?.profilePicture ||
      essProfile?.avatar ||
      null;

    if (!raw || typeof raw !== 'string') return null;
    if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('data:')) {
      return raw;
    }
    if (raw.startsWith('/')) {
      return `${process.env.REACT_APP_API_BASE_URL || ''}${raw}`;
    }
    return `${process.env.REACT_APP_API_BASE_URL || ''}/uploads/${raw}`;
  };

  const profileImgSrc = getProfileImgSrc();

  const displayEmployeeId =
    user?.employeeId ||
    user?.empId ||
    userWithPermissions?.employeeId ||
    userWithPermissions?.empId ||
    essProfile?.employeeId ||
    (user?._id ? user._id.slice(-8) : null) ||
    '122256';

  const displayName =
    user?.firstName
      ? `${user.firstName} ${user.lastName || ''}`.trim()
      : essProfile?.personalInfo?.firstName
      ? `${essProfile.personalInfo.firstName} ${essProfile.personalInfo.lastName || ''}`.trim()
      : user?.email || 'User';

  return (
    <header className="bg-white border-b border-[#EEF2F7] px-3.5 sm:px-5 py-2 flex items-center justify-between shadow-xs h-[56px] z-30 transition-colors relative">
      {/* Left Section */}
      <div className="flex items-center gap-2.5 sm:gap-3 flex-shrink-0">
        <button
          type="button"
          onClick={onToggleSidebar}
          className="w-8 h-8 rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] flex items-center justify-center text-[#475569] transition-colors focus:outline-none flex-shrink-0"
          title="Toggle Navigation Sidebar"
        >
          <Menu className="h-4 w-4 stroke-[2]" />
        </button>
      </div>

      {/* Center Section - Search Bar Container */}
      <div className="flex-1 flex justify-center items-center px-2 sm:px-4 min-w-0" ref={searchRef}>
        {isSearchOpen && (
          <div className="w-full max-w-[340px] md:max-w-[440px] lg:max-w-[500px] animate-in fade-in duration-200">
            <SearchMenu
              searchTerm={searchTerm}
              onSearchChange={onSearchChange}
              isCollapsed={false}
              autoFocus
            />
          </div>
        )}
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-1.5 sm:gap-2.5 flex-shrink-0">
        {/* Search Toggle Icon - Positioned closest to AI Copilot */}
        <button
          type="button"
          onClick={() => setIsSearchOpen(!isSearchOpen)}
          className={`search-toggle-btn w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none flex-shrink-0 cursor-pointer shadow-2xs ${
            isSearchOpen
              ? 'bg-blue-50 text-blue-600 border border-blue-200'
              : 'bg-[#F8FAFC] hover:bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1]'
          }`}
          title={isSearchOpen ? "Close Search" : "Search Page (Ctrl+K)"}
        >
          {isSearchOpen ? <X className="h-4 w-4 stroke-[2]" /> : <Search className="h-4 w-4 text-slate-500 stroke-[2]" />}
        </button>

        {/* Copilot AI Assistant — navigates to full AI Copilot page */}
        <button
          type="button"
          id="sara-ai-header-btn"
          onClick={() => navigate('/ai-copilot')}
          className="px-2.5 sm:px-3.5 py-1.5 rounded-full bg-[#1D4ED8] hover:bg-[#1E40AF] text-white shadow-md hover:shadow-lg flex items-center gap-1.5 sm:gap-2 transition-all duration-200 focus:outline-none cursor-pointer active:scale-95 border border-white/20 flex-shrink-0"
          aria-label="Open AI Copilot"
        >
          <AICopilotAvatar size={18} />
          <span className="hidden xs:inline text-xs sm:text-[13px] font-bold tracking-tight text-white font-sans whitespace-nowrap">AI Copilot</span>
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0 ml-0.5" />
        </button>

        {/* Notification Dropdown */}
        <NotificationDropdown />

        {/* User Profile Block */}
        <div className="relative" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#F8FAFC] hover:bg-[#F1F5F9] border border-[#E2E8F0] transition-all duration-150 focus:outline-none shadow-2xs"
          >
            <div className="relative w-7 h-7 rounded-full bg-[#0F2942] text-white flex items-center justify-center overflow-hidden border border-[#CBD5E1] flex-shrink-0">
              {profileImgSrc ? (
                <img src={profileImgSrc} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <User className="h-3.5 w-3.5 stroke-[2]" />
              )}
            </div>
            <div className="hidden md:flex flex-col text-left">
              <span className="text-keka-small font-semibold text-[#0F2942] leading-tight">
                {displayName}
              </span>
              <span className="text-[10px] font-medium text-[#64748B] mt-0.5">
                {formatRoleName(user?.role)}
              </span>
            </div>
            <ChevronDown className="h-3.5 w-3.5 text-[#64748B] ml-0.5 stroke-[2]" />
          </button>

          {/* User Profile Dropdown Menu (Exact match to user's Image 2 reference) */}
          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-80 sm:w-[340px] bg-white rounded-xl shadow-2xl border border-gray-200 z-50 p-4">
              {/* Close Button at Top Right */}
              <button
                type="button"
                onClick={() => setShowUserMenu(false)}
                className="absolute top-3.5 right-3.5 w-6 h-6 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 transition-colors"
              >
                <X className="w-3.5 h-3.5 stroke-[2]" />
              </button>

              {/* User Photo & Info Block */}
              <div className="flex items-start gap-3.5 pr-6">
                <div className="w-16 h-16 rounded-xl bg-[#0F2942] text-white flex items-center justify-center overflow-hidden border border-gray-200 flex-shrink-0 shadow-xs">
                  {profileImgSrc ? (
                    <img src={profileImgSrc} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <User className="h-8 w-8 stroke-[1.5]" />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <h4 className="text-[15px] font-bold text-gray-900 leading-tight truncate tracking-keka-heading">
                    {displayName}
                  </h4>
                  <div className="text-keka-caption text-gray-600 mt-1 flex items-center gap-1">
                    <span>User Id: {displayEmployeeId}</span>
                    <Info className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  </div>
                  <p className="text-keka-caption text-gray-600 mt-0.5 truncate">
                    {user?.email || essProfile?.personalInfo?.email || 'user@mobiloitte.com'}
                  </p>
                </div>
              </div>

              {/* Bottom Action Row: My Account & Sign Out */}
              <div className="mt-5 pt-2 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    navigate('/my_profile');
                    setShowUserMenu(false);
                  }}
                  className="px-3.5 py-1.5 border border-[#0F2942] text-[#0F2942] font-semibold text-keka-small rounded-lg hover:bg-blue-50 transition-colors"
                >
                  My Account
                </button>
                <button
                  type="button"
                  onClick={handleLogoutClick}
                  className="flex items-center gap-1.5 text-red-500 hover:text-red-700 font-semibold text-keka-small transition-colors"
                >
                  <LogOut className="w-4 h-4 text-red-500 stroke-[2]" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Logout Confirmation Alert */}
      <Alert
        isOpen={showLogoutAlert}
        onClose={() => setShowLogoutAlert(false)}
        title="Confirm Logout"
        message="Are you sure you want to logout?"
        type="warning"
        showCancel={true}
        confirmText="YES"
        cancelText="NO"
        onConfirm={onLogout}
        onCancel={() => setShowLogoutAlert(false)}
      />
    </header>
  );
};

export default Header;
